<script setup lang="ts">
import {
  NXGRetailInfoListVariants,
  type NXGRetailInfoListRow,
  type NXGRetailInfoListVariant
} from '#src/components/info-list/types'

const { variant = NXGRetailInfoListVariants.card } = defineProps<{
  data: NXGRetailInfoListRow[]
  /**
   * `card` — standalone padded surface with rounded corners (default).
   * `section` — row list only; no container padding; parent owns border and radius.
   * Row label/value padding is unchanged in both variants.
   */
  variant?: NXGRetailInfoListVariant
}>()
</script>

<template>
  <div
    class="nxg-retail-info-list"
    :class="{ 'nxg-retail-info-list--section': variant === NXGRetailInfoListVariants.section }"
  >
    <div v-for="row in data" :key="row.key" class="nxg-retail-info-list__row">
      <div class="nxg-retail-info-list__key">
        <slot :name="`label-${row.key}`" :row>
          <slot name="label" :row>
            {{ row.label }}
          </slot>
        </slot>
      </div>
      <div class="nxg-retail-info-list__value">
        <slot :name="`value-${row.key}`" :row>
          <slot name="value" :row>
            {{ row.value }}
          </slot>
        </slot>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@nsftx/seven-nxg-design-system/style/mixins' as *;

.nxg-retail-info-list {
  display: flex;
  flex-direction: column;
  border-radius: var(--nxgds-alias-radius-corner-theme);
  background: var(--nxgds-mapped-color-surface-component-card-default);
  padding: var(--nxgds-alias-spacing-lg);
  height: fit-content;

  &--section {
    padding: 0;
    border-radius: 0;
  }

  &__row {
    display: grid;
    grid-template-columns: 200px 1fr;
    border-bottom: 1px solid var(--nxgds-mapped-color-border-divider);

    &:last-child {
      border-bottom: none;
    }
  }

  &__key {
    color: var(--nxgds-mapped-color-text-secondary);
    padding: var(--nxgds-alias-spacing-sm);

    @include typography('paragraph', 'regular');
  }

  &__value {
    color: var(--nxgds-mapped-color-text-primary);
    padding: var(--nxgds-alias-spacing-sm);

    @include typography('paragraph', 'strong');
  }
}
</style>
